import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import { subscribeQueueEvents } from '../../utils/queueEventBus';
import { currentLocale } from '../../i18n';
import './QueueChat.css';

const MAX_LEN = 500;

/**
 * Ephemeral chat for the active queue cohort.
 *
 * Backlog is fetched once on mount, then kept in sync via the global queue
 * event bus (populated by useQueueWebSocket). Chat is wiped server-side
 * when a match starts (all accepted) or when an admin runs `/queue clear`;
 * we mirror that by listening for the `cleared` action and dropping our
 * local state, so users don't see stale messages from the previous queue.
 *
 * @param {object} props
 * @param {string} props.guildId - Guild whose queue chat to render.
 */
export default function QueueChat({ guildId }) {
  const { t } = useTranslation();
  const { isLoggedIn, getAuthHeaders, login } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const listRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  useEffect(() => {
    if (!guildId) return;
    let cancelled = false;
    fetch(`${API_URL}/api/queue/${guildId}/chat`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        const fetched = Array.isArray(data?.messages) ? data.messages : [];
        // Merge instead of overwrite: a WS event could land between mount and
        // fetch completion, and a plain overwrite would drop it.
        setMessages((current) => {
          if (current.length === 0) return fetched;
          const ids = new Set(fetched.map((m) => m.id));
          return [...fetched, ...current.filter((m) => !ids.has(m.id))];
        });
        requestAnimationFrame(scrollToBottom);
      })
      .catch(() => {
        if (!cancelled) setMessages((current) => current);
      });
    return () => {
      cancelled = true;
    };
  }, [guildId, scrollToBottom]);

  useEffect(() => {
    if (!guildId) return;
    return subscribeQueueEvents((data) => {
      if (data?.event !== 'queue_chat') return;
      if (data.action === 'cleared') {
        setMessages([]);
        return;
      }
      if (data.action === 'message' && data.message) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === data.message.id)) return prev;
          return [...prev, data.message];
        });
        requestAnimationFrame(scrollToBottom);
      }
    });
  }, [guildId, scrollToBottom]);

  const handleSend = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    if (!isLoggedIn) {
      login();
      return;
    }
    setSending(true);
    setError(null);
    try {
      const r = await fetch(`${API_URL}/api/queue/${guildId}/chat`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        const inner = body && typeof body.detail === 'object' ? body.detail : body;
        const msg =
          (inner && inner.error) ||
          (typeof body.detail === 'string' ? body.detail : null) ||
          t('queue.sendFailedDefault');
        setError(msg);
        return;
      }
      setInput('');
    } catch {
      setError(t('queue.networkError'));
    } finally {
      setSending(false);
    }
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts * 1000);
    return d.toLocaleTimeString(currentLocale(), { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="queue-chat-card">
      <div className="queue-chat-header">
        <h2 className="queue-chat-title">{t('queue.chatTitle')}</h2>
        <span className="queue-chat-hint">{t('queue.chatHint')}</span>
      </div>

      <div className="queue-chat-messages" ref={listRef}>
        {messages.length === 0 ? (
          <div className="queue-chat-empty">{t('queue.chatEmpty')}</div>
        ) : (
          messages.map((m) => (
            <div key={m.id} className="queue-chat-message">
              {m.avatar && (
                <img src={m.avatar} alt="" className="queue-chat-avatar" />
              )}
              <div className="queue-chat-body">
                <div className="queue-chat-meta">
                  <span className="queue-chat-author">{m.author_name}</span>
                  <span className="queue-chat-time">{formatTime(m.ts)}</span>
                </div>
                <div className="queue-chat-text">{m.text}</div>
              </div>
            </div>
          ))
        )}
      </div>

      <form className="queue-chat-form" onSubmit={handleSend}>
        <input
          type="text"
          className="queue-chat-input"
          placeholder={isLoggedIn ? t('queue.chatPlaceholder') : t('queue.chatLoginPlaceholder')}
          value={input}
          onChange={(e) => setInput(e.target.value.slice(0, MAX_LEN))}
          maxLength={MAX_LEN}
          disabled={!isLoggedIn || sending}
        />
        <button
          type="submit"
          className="queue-chat-send"
          disabled={!isLoggedIn || sending || !input.trim()}
        >
          {t('queue.send')}
        </button>
      </form>

      {error && <div className="queue-chat-error">{error}</div>}
    </div>
  );
}
